import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  DeleteOnedata,
  StringLimit,
  capitalize,
  getFilterDataWithPagination,
  separateDasFromUrl,
} from "../../controllers/APIController";
import { getDataWithFilterUrl } from "../../controllers/APIController";
import {
  Button,
  Chip,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  useDisclosure,
  Input,
} from "@nextui-org/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faSearch, 
  faPlus, 
  faSort, 
  faCog, 
  faChartLine,
  faDatabase,
  faCheckCircle,
  faClock,
  faTags,
  faEye,
  faEdit,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

import UnifiedDeleteModal from "../../components/UnifiedDeleteModal";
import LoadingPage from "../../components/Loader/LoadingPage";
import {
  decreasedMetricCurrentCount,
  getSchemaForOrg,
  syncMetricCurrentCount,
  UserCell,
  DeleteTableItemWithOpenModal,
  DeleteTableItemWithUnifiedModal,
} from "../../utils/helper";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import NullData from "../../components/ErrorPage/NullData";

import SendSurveyModal from "../External/SendSurveyModal";
import MetricInfo from "./MetricInfo";

import ExportModal from "./ExportModal";

/**
 * Sorting options for metric entries
 * Defines the available sorting methods for the metrics table
 */
const sortingItemList = [
  {
    key: "desc",
    name: "Recent",
    order: "desc",
  },
  {
    key: "asc",
    name: "Oldest",
    order: "asc",
  },
];

/**
 * SingleTypemetrics Component
 * Displays a detailed view of metrics for a specific category and metric type
 * Provides functionality for viewing, creating, editing, and deleting metric entries
 * Includes search, filtering, sorting, and export capabilities
 */
const SingleTypemetrics = () => {
  // State for collection and metric information
  const [collectionInfo, setCollectionInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMetricLoading, setIsMetricLoading] = useState(true);
  const [frequencyReport, setFrequencyReport] = useState({});

  // Get description from collection info
  let description = collectionInfo?.attributes?.Description;
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const itemsPerPage = 10;
  
  // Data state
  const [metricsList, setMetricsList] = useState([]);
  const [reRenderPage, setReRenderPage] = useState(0);
  
  // Delete modal state
  const [deleteMetricId, setDeleteMetricId] = useState(-1);
  const [deleteItemData, setDeleteItemData] = useState({ id: -1, name: "" });
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [sortedBy, setSortedBy] = useState(sortingItemList[0].key);
  
  // Navigation and routing
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { _orgId, _categoryId, _metricId } = useParams();

  // Modal disclosure hooks
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const {
    isOpen: isOpenSurvey,
    onOpen: onOpenSurvey,
    onOpenChange: onOpenChangeSurvey,
  } = useDisclosure();

  const {
    isOpen: isOpenExportModal,
    onOpen: onOpenExportModal,
    onOpenChange: onOpenChangeExportModal,
  } = useDisclosure();

  // User and organization information
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const userId = userLogged && userLogged.user.id;
  const orgInfo = userLogged && userLogged.user.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + orgInfo;

  // Survey-specific logic
  const isSurvey = _categoryId === "survey" || _categoryId === "vendor-details";
  const buttonTextForSurvey =
    _categoryId === "survey"
      ? "Survey"
      : _categoryId === "vendor-details"
      ? "Vendor"
      : "";

  // Metric title and filter URL
  let headingTitle = capitalize(separateDasFromUrl(_metricId));
  let filteredUrl = orgFilterUrl + "&filters[Metric_Id][$eqi]=" + headingTitle;

  // Customizable table columns state
  const [customizeCompleteList, setCustomizeCompleteList] = useState([]);
  const [customizeItems, setCustomizeItems] = useState(new Set([]));

  // Generate header list for table columns
  const customizeHeaderList = Array.from(customizeItems).map((name) => {
    return {
      id: name?.toLowerCase(),
      label: name,
    };
  });

  /**
   * Filter data based on search term
   * Searches across JsonSchema properties and attributes
   */
  const filteredData = metricsList.filter((item) => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Search in JsonSchema properties (new grouped structure)
    if (item?.attributes?.JsonSchema?.flatData) {
      for (const key in item?.attributes?.JsonSchema?.flatData) {
        const value = item?.attributes?.JsonSchema?.flatData[key];
        if (typeof value === 'string' && value.toLowerCase().includes(searchLower)) {
          return true;
        }
      }
    }
    
    // Search in JsonSchema properties (legacy format)
    for (const key in item?.attributes?.JsonSchema) {
      const value = item?.attributes?.JsonSchema[key];
      if (typeof value === 'string' && value.toLowerCase().includes(searchLower)) {
        return true;
      }
    }
    
    // Search in direct attributes
    for (const key in item?.attributes) {
      const value = item?.attributes[key];
      if (typeof value === 'string' && value.toLowerCase().includes(searchLower)) {
        return true;
      }
    }
    
    return false;
  });

  /**
   * Navigate to create new metric entry
   */
  const navigateToCreate = () => {
    navigate(pathname + "/create");
  };

  /**
   * Navigate to edit existing metric entry
   * @param {number} id - ID of the metric entry to edit
   */
  const editHandler = (id) => {
    navigate(pathname + `/${id}`);
  };

  /**
   * Handle deletion of metric entry
   * Deletes the entry and updates the metric counter
   */
  const deleteHandler = async () => {
    const result = await DeleteOnedata("responses", deleteItemData.id);
    await decreasedMetricCurrentCount(
      collectionInfo?.id,
      result?.data?.attributes?.createdAt
    );
    setReRenderPage((prev) => 1 - prev);
  };

  /**
   * Fetch metrics data with pagination and sorting
   * Updates metrics list and pagination information
   */
  useEffect(() => {
    const newUrl = filteredUrl + `&sort[0]=updatedAt:${sortedBy}`;
    getFilterDataWithPagination(
      "responses",
      newUrl,
      currentPage,
      itemsPerPage,
      orgInfo
    )
      .then((data) => {
        // Ensure data exists and has the expected structure
        if (!data || !data.meta?.pagination) {
          console.warn("Invalid pagination data structure:", data);
          setTotalPage(1);
          setMetricsList([]);
          setIsLoading(false);
          return;
        }
        
        setTotalPage(data.meta.pagination.pageCount || 1);
        setMetricsList(data.data || []);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching metrics data:", error);
        // Set fallback values to prevent crashes
        setTotalPage(1);
        setMetricsList([]);
        setIsLoading(false);
      });
  }, [reRenderPage, currentPage, sortedBy, filteredUrl, orgInfo]);

  /**
   * Fetch metric collection information and set up table structure
   * Handles single submission forms and sets up customizable columns
   */
  useEffect(() => {
    const url = `${orgFilterUrl}&filters[Name][$eqi]=${headingTitle}`;
    getDataWithFilterUrl("metrics", url)
      .then((data) => {
        // Ensure data exists and has the expected structure
        // Handle both direct array and nested {data: Array, meta: {...}} structure
        const metricData = data?.data || data;
        
        if (!metricData || !Array.isArray(metricData) || metricData.length === 0) {
          console.warn("No metric data found or invalid structure:", data);
          setIsMetricLoading(false);
          return;
        }

        const firstMetric = metricData[0];
        if (!firstMetric?.attributes?.JsonSchema?.properties) {
          console.warn("Invalid metric structure - missing JsonSchema properties:", firstMetric);
          setIsMetricLoading(false);
          return;
        }

        const responseData = firstMetric.attributes.JsonSchema.properties;
        const isSingleSubmittedForm = firstMetric.attributes.FrequencyValue === 0;

        if (isSingleSubmittedForm) {
          // For single submission forms, show the page and let user manually navigate
          // Don't auto-redirect - let user choose to create or edit
          console.log("Single submission form - showing page without auto-redirect");
        }
        
        let headerList = [];
        const formulaHeading =
          firstMetric.attributes.JsonSchema.formula?.map(
            (item) => item?.title
          ) || [];
        
        // Debug logging to see what we're working with
        console.log("Raw JsonSchema properties:", responseData);
        console.log("Formula headings:", formulaHeading);
        
        // Get organization-specific schema
        getSchemaForOrg(responseData, orgInfo).then((schemaData) => {
          console.log("Schema data from getSchemaForOrg:", schemaData);
          
          // Extract headers from schema - use schemaData parameter, not data variable
          if (schemaData && Array.isArray(schemaData)) {
            schemaData.forEach((row) => {
              if (row && row[0]) {
                headerList.push(row[0]);
              }
            });
          }
          
          // Also add direct property names as fallback
          if (responseData) {
            Object.keys(responseData).forEach(key => {
              if (!headerList.includes(key)) {
                headerList.push(key);
              }
            });
          }
          
          console.log("Final header list:", headerList);
          
          const allHeaders = [
            ...headerList,
            ...formulaHeading,
            "Status",
          ];
          
          console.log("All headers including formulas and status:", allHeaders);
          
          setCustomizeCompleteList(allHeaders);

          // Set first 10 columns as default visible columns
          const firstTenColumns = allHeaders.filter((item, index) => {
            return index < 10;
          });
          setCustomizeItems(new Set(firstTenColumns));
        });

        let responseArr = Object.keys(responseData).map((key) => [
          key,
          responseData[key],
        ]);

        responseArr.sort((a, b) => {
          return a[1].id - b[1].id;
        });
        setCollectionInfo(firstMetric);
        setIsMetricLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching metric collection info:", error);
        setIsMetricLoading(false);
      });
  }, [orgFilterUrl, headingTitle, orgInfo, navigate]);

  /**
   * Sync metric counter and update frequency report
   * Ensures the displayed count matches the actual database count
   */
  useEffect(() => {
    if (collectionInfo === null) return;
    
    const syncAndUpdateDisplay = async () => {
      try {
        // Sync the counter with actual database count
        await syncMetricCurrentCount(
          collectionInfo?.id,
          orgInfo,
          collectionInfo?.attributes?.Name
        );
        
        // Refetch the updated metric data
        const url = `${orgFilterUrl}&filters[Name][$eqi]=${headingTitle}`;
        const updatedData = await getDataWithFilterUrl("metrics", url);
        const updatedCollectionInfo = updatedData.data[0];
        
        if (updatedCollectionInfo) {
          const currCount = updatedCollectionInfo?.attributes?.currentCounter;
          const requiredCount = updatedCollectionInfo?.attributes?.requiredCounter;

          const frequencyData = {
            Status: currCount >= requiredCount ? "Completed" : "Incompleted",
            RequiredCount: requiredCount,
            CurrentCount: currCount,
            lastUpdatedAt: updatedCollectionInfo?.attributes?.lastUpdatedReponse,
          };
          setFrequencyReport(frequencyData);
        }
      } catch (error) {
        console.error("Error syncing metric count:", error);
        // Fallback to original data if sync fails
        const currCount = collectionInfo?.attributes?.currentCounter;
        const requiredCount = collectionInfo?.attributes?.requiredCounter;

        const frequencyData = {
          Status: currCount >= requiredCount ? "Completed" : "Incompleted",
          RequiredCount: requiredCount,
          CurrentCount: currCount,
          lastUpdatedAt: collectionInfo?.attributes?.lastUpdatedReponse,
        };
        setFrequencyReport(frequencyData);
      }
    };

    syncAndUpdateDisplay();
  }, [collectionInfo, orgInfo, orgFilterUrl, headingTitle]);

  /**
   * Get the current metric entry being deleted for display in modal
   * Attempts to find a meaningful display name from the metric data
   */
  const currentMetricToDelete = metricsList?.find(item => item.id === deleteMetricId);
  const getMetricDisplayName = () => {
    if (!currentMetricToDelete) return "this entry";
    
    // Try to get a meaningful display name from the metric data
    const attributes = currentMetricToDelete.attributes;
    const jsonSchema = attributes?.JsonSchema;
    
    // Look for common identifying fields in the data
    const possibleNameFields = ['name', 'title', 'Name', 'Title', 'description', 'Description'];
    let displayName = null;
    
    // Check in JsonSchema first
    if (jsonSchema) {
      // Check in flatData if it exists (new grouped structure)
      if (jsonSchema.flatData) {
        for (const field of possibleNameFields) {
          if (jsonSchema.flatData[field] && jsonSchema.flatData[field] !== "null") {
            displayName = jsonSchema.flatData[field];
            break;
          }
        }
      }
      
      // Check directly in JsonSchema if not found in flatData
      if (!displayName) {
        for (const field of possibleNameFields) {
          if (jsonSchema[field] && jsonSchema[field] !== "null") {
            displayName = jsonSchema[field];
            break;
          }
        }
      }
    }
    
    // Check directly in attributes if not found in JsonSchema
    if (!displayName) {
      for (const field of possibleNameFields) {
        if (attributes[field] && attributes[field] !== "null") {
          displayName = attributes[field];
          break;
        }
      }
    }
    
    // Fallback to entry ID if no name found
    return displayName || `Entry #${deleteMetricId}`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page Header */}
      <div className="bg-white border-b border-slate-100 px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between max-w-full">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">{headingTitle}</h1>
              <p className="text-xs text-slate-600 mt-1">{description && description}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Survey-specific button */}
            {isSurvey && (
              <Button
                color="primary"
                onClick={() => onOpenSurvey()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 font-medium"
                size="sm"
              >
                Send {buttonTextForSurvey}
              </Button>
            )}
            {/* Create entry button for non-survey metrics */}
            {!isSurvey && (
              <Button
                color="primary"
                onClick={navigateToCreate}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 font-medium"
                startContent={<FontAwesomeIcon icon={faPlus} className="text-white text-xs" />}
                size="sm"
              >
                Create Entry
              </Button>
            )}
          </div>
        </div>
      </div>

      <BreadCrumbsPage>
        <div>
          <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Delete Confirmation Modal */}
          <UnifiedDeleteModal
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            onConfirm={deleteHandler}
            title="Delete Metric Entry"
            message="Are you sure you want to delete"
            confirmText="Delete Entry"
            cancelText="Cancel"
            itemName={deleteItemData.name || getMetricDisplayName()}
          />
          
          {/* Send Survey Modal */}
          <SendSurveyModal
            isOpen={isOpenSurvey}
            onOpen={onOpenSurvey}
            onOpenChange={onOpenChangeSurvey}
            metricID={_metricId}
          />
          
          {/* Export Modal */}
          <ExportModal
            isOpen={isOpenExportModal}
            onOpen={onOpenExportModal}
            onOpenChange={onOpenChangeExportModal}
            data={metricsList}
            metricName={collectionInfo?.attributes?.Name}
            formFieldsList={customizeCompleteList}
          />

          {/* Metrics Repository Section */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                {/* Search and Filter Section */}
                <div className="bg-white">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    {/* Search Input */}
                    <div className="flex-1 w-full sm:w-auto">
                      <Input
                        placeholder={`Search ${headingTitle} entries...`}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        startContent={<FontAwesomeIcon icon={faSearch} className="text-slate-400 text-sm" />}
                        size="sm"
                        className="w-full sm:max-w-md"
                      />
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-3">
                      {/* Export Button */}
                      {customizeCompleteList?.length !== 0 && (
                        <Button
                          size="sm"
                          variant="bordered"
                          className="text-slate-600 hover:text-slate-900 border-slate-200"
                          onClick={onOpenExportModal}
                        >
                          Export
                        </Button>
                      )}

                      {/* Sort Dropdown */}
                      <Dropdown className="w-max">
                        <DropdownTrigger>
                          <Button
                            size="sm"
                            variant="bordered"
                            startContent={<FontAwesomeIcon icon={faSort} className="text-slate-600 text-sm" />}
                            className="text-slate-600 hover:text-slate-900 border-slate-200"
                          >
                            Sort
                          </Button>
                        </DropdownTrigger>
                        <DropdownMenu
                          aria-label="Static Actions"
                          variant="flat"
                          selectionMode="single"
                          defaultSelectedKeys={new Set([sortedBy])}
                          selectedKeys={new Set([sortedBy])}
                          onSelectionChange={(event) => {
                            if (
                              Array.from(event).join(", ").replaceAll("_", " ") === ""
                            )
                              return;
                            setSortedBy(
                              Array.from(event).join(", ").replaceAll("_", " ")
                            );
                          }}
                        >
                          {sortingItemList?.map((item) => (
                            <DropdownItem key={item.key}>{item.name}</DropdownItem>
                          ))}
                        </DropdownMenu>
                      </Dropdown>
                      
                      {/* Customize Columns Dropdown */}
                      <Dropdown className="w-max">
                        <DropdownTrigger>
                          <Button
                            size="sm"
                            variant="bordered"
                            startContent={<FontAwesomeIcon icon={faCog} className="text-slate-600 text-sm" />}
                            className="text-slate-600 hover:text-slate-900 border-slate-200"
                          >
                            Customize
                          </Button>
                        </DropdownTrigger>
                        <DropdownMenu
                          aria-label="Multiple selection"
                          variant="flat"
                          closeOnSelect={false}
                          selectionMode="multiple"
                          disallowEmptySelection
                          selectedKeys={customizeItems}
                          onSelectionChange={setCustomizeItems}
                          className="custom-dropdown-menu"
                        >
                          {customizeCompleteList?.map((name) => (
                            <DropdownItem key={name}>{name}</DropdownItem>
                          ))}
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  </div>
                </div>
            </div>
            
            {/* Table Section */}
            <div className="p-6">
              <LoadingPage
                isLoading={
                  isLoading ||
                  isMetricLoading ||
                  customizeCompleteList?.length === 0
                }
              >
                <div className="overflow-x-auto">
                  {/* Metrics Data Table */}
                  <Table
                    removeWrapper
                    selectionMode="single"
                    classNames={{
                      wrapper: "shadow-none",
                      th: "bg-slate-50 text-slate-700 font-semibold text-sm border-b border-slate-200",
                      td: "border-b border-slate-100 py-4",
                      tr: "hover:bg-slate-50 transition-colors",
                    }}
                    aria-label="single-metric-table"
                  >
                    {/* Table Header */}
                    <TableHeader>
                      {customizeHeaderList.map((header) => (
                        <TableColumn key={header.id}>
                          {StringLimit(header.label, 80)}
                        </TableColumn>
                      ))}
                      <TableColumn key={customizeCompleteList.length + 1}>
                        Submitted By
                      </TableColumn>
                      <TableColumn key={customizeCompleteList.length + 2}>
                        Actions
                      </TableColumn>
                    </TableHeader>
                    
                    {/* Table Body */}
                    <TableBody emptyContent={<NullData />}>
                      {metricsList?.length !== 0 &&
                        metricsList.map((item, index) => {
                          return (
                            <TableRow
                              key={item.id}
                              onClick={() => editHandler(item.id)}
                              className="cursor-pointer hover:bg-slate-50 transition-colors"
                            >
                              {/* Dynamic columns based on customization */}
                              {customizeHeaderList.map(({ label: key }) => {
                                // Debug logging for each field
                                console.log(`Rendering field "${key}" for item ${item.id}:`, {
                                  directValue: item?.attributes?.JsonSchema[key],
                                  flatDataValue: item?.attributes?.JsonSchema?.flatData?.[key],
                                  legacyValue: item?.attributes[key]
                                });
                                
                                // Handle new grouped data structure and legacy format
                                let fieldValue = item?.attributes?.JsonSchema[key] || item?.attributes[key];
                                
                                // Check if new grouped structure exists
                                if (item?.attributes?.JsonSchema?.flatData) {
                                  fieldValue = item?.attributes?.JsonSchema?.flatData[key];
                                }
                                
                                if (fieldValue === "null" || fieldValue === null) {
                                  return <TableCell key={key}>--</TableCell>;
                                }
                                
                                // Handle file attachments
                                if (typeof fieldValue === "object" && fieldValue?.url) {
                                  return (
                                    <TableCell key={key}>
                                      <lord-icon
                                        src="https://cdn.lordicon.com/rbbnmpcf.json"
                                        trigger="hover"
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                        }}
                                        className="cursor-grab"
                                      ></lord-icon>
                                    </TableCell>
                                  );
                                } else {
                                  return (
                                    <TableCell key={key}>
                                      {typeof fieldValue === "string" ? StringLimit(fieldValue, 80) : fieldValue}
                                    </TableCell>
                                  );
                                }
                              })}

                              {/* User information cell */}
                              {UserCell(
                                item?.attributes?.users_permissions_user?.data
                                  ?.attributes?.email,
                                item.attributes?.users_permissions_user?.data?.id
                              )}
                              
                              {/* Delete action cell */}
                              {DeleteTableItemWithUnifiedModal(
                                item.id,
                                item.attributes?.users_permissions_user?.data?.id ===
                                  userId,
                                setDeleteItemData,
                                onOpen,
                                getMetricDisplayName()
                              )}
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Pagination */}
                {metricsList?.length > 0 && (
                  <div className="w-full mt-8 pt-6 border-t border-slate-200 flex items-center justify-between">
                    <div className="text-sm text-slate-600">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} entries
                    </div>
                    <Pagination
                      showControls
                      total={totalPage}
                      color="primary"
                      page={currentPage}
                      onChange={setCurrentPage}
                      size="sm"
                      classNames={{
                        wrapper: "gap-0 overflow-visible h-8",
                        item: "w-8 h-8 text-small rounded-none bg-transparent",
                        cursor: "bg-emerald-600 shadow-lg from-emerald-600 to-emerald-700 text-white font-bold",
                      }}
                    />
                  </div>
                )}
              </LoadingPage>
            </div>
          </div>
        </div>
        </div>
      </div>
    </BreadCrumbsPage>
  </div>
  );
};

export default SingleTypemetrics;